'use client';

// Global banner shown above the navbar when the signed-in user owns at
// least one site but has no valid subscription (cancelled, transferred,
// or never subscribed). The banner links to /profile#subscription where
// the user picks their phase (single / three) and billing cadence
// (monthly / yearly) before being forwarded to Chargebee checkout —
// going direct to checkout from here would skip the plan choice and
// risk subscribing customers to the wrong plan.
//
// Suppressed for:
//   - Admins / installers (internal staff)
//   - Active / in_trial / non_renewing / paused / future subscriptions
//     (covered by the "Manage subscription" button on /profile)
//   - Users who do NOT own a site. A site has at most one
//     `owner_user_id`; anyone else with access (read-only viewers,
//     household members, installer staff looking at a customer page)
//     piggy-backs on the owner's subscription and shouldn't be nagged.
//     We confirm ownership by checking /api/sites for at least one
//     site where `owner_user_id === user.id` before rendering.
//   - Owners of a site whose `installer_id` is in
//     `SUBSCRIPTION_EXEMPT_INSTALLER_IDS`. These installers bundle the
//     Powston subscription into their own offering, so the customer
//     doesn't pay Powston directly and shouldn't be nagged.

import Link from 'next/link';
import { useState } from 'react';
import { CreditCard, X } from 'lucide-react';
import { useOptionalUser } from '@/context/UserContext';
import { isActiveLikeStatus, isTerminatedStatus, useSiteOwnership } from '@/app/lib/subscriptionAccess';

const DISMISSED_KEY = 'powston_subscription_banner_dismissed_until';
// Dismiss duration — banner reappears after this many ms so users can't
// permanently silence it.
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000;

const SubscriptionBanner: React.FC = () => {
  // useOptionalUser instead of useUser — this component mounts at the
  // layout level which wraps every page, including public ones (login,
  // signup) where UserProvider doesn't install a context. Soft-fail to
  // null and render nothing rather than crash the prerender / page.
  const ctx = useOptionalUser();
  const [dismissed, setDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    const until = Number(window.localStorage.getItem(DISMISSED_KEY) || 0);
    return Date.now() < until;
  });
  const userId = ctx?.user?.id;
  const status = (ctx?.user?.subscription_status ?? '').toLowerCase();
  const isActiveLike = isActiveLikeStatus(status);
  const isAdmin = !!ctx?.isAdmin;
  const isInstaller = !!ctx?.isInstaller;
  // Only bother querying /api/sites when the banner might actually fire.
  // Staff / active-like / dismissed cases short-circuit without a fetch.
  const mightShow = !!userId && !isAdmin && !isInstaller && !isActiveLike && !dismissed;
  // `null` while we haven't decided yet — keeps the banner hidden until
  // we've confirmed the user actually owns a site that needs a sub.
  const ownership = useSiteOwnership(mightShow);

  if (!ctx?.user) return null;
  const { user } = ctx;
  // Admins and installers are internal/staff — they don't have personal
  // subscriptions tied to the customer-facing Powston product. Suppress the
  // banner for them so they're not nagged when looking at customer pages.
  if (isAdmin || isInstaller) return null;
  // Active or pending-active states — nothing to nag about.
  if (isActiveLike) return null;
  if (dismissed) return null;
  // Subscriptions are only required for *site owners*. A user who can only
  // view someone else's site (read-only access, family member, installer's
  // customer page, etc.) doesn't pay — they piggy-back on the owner's sub.
  // Wait until ownership is known, then suppress for non-owners.
  if (!ownership?.ownsSite) return null;
  // Owners on sites tied to a subscription-bundled installer are billed via
  // the installer and shouldn't be nagged.
  if (ownership.exemptByInstaller) return null;

  const isCancelled = isTerminatedStatus(status);
  const headline = isCancelled
    ? 'Your Powston subscription has ended'
    : 'Your account doesn’t have an active subscription';
  const sub = isCancelled
    ? 'Reactivate to keep Powston controlling your inverter and trading on your behalf.'
    : 'Subscribe to enable Powston control and start trading.';

  const dismiss = () => {
    setDismissed(true);
    try {
      window.localStorage.setItem(DISMISSED_KEY, String(Date.now() + DISMISS_DURATION_MS));
    } catch { /* private mode / blocked storage */ }
  };

  return (
    <div className="w-full bg-amber-50 dark:bg-amber-900/30 border-b border-amber-200 dark:border-amber-800/60">
      <div className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-2 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-2 min-w-0 flex-1">
          <CreditCard className="w-4 h-4 text-amber-700 dark:text-amber-400 mt-0.5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="text-sm font-semibold text-amber-900 dark:text-amber-200">{headline}</p>
            <p className="text-xs text-amber-800 dark:text-amber-300 leading-snug">{sub}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <Link
            href="/profile#subscription"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-md bg-emerald-600 text-white hover:bg-emerald-700 transition-colors"
          >
            {isCancelled ? 'Reactivate subscription' : 'Subscribe'}
          </Link>
          <button
            onClick={dismiss}
            aria-label="Dismiss for 24 hours"
            title="Dismiss for 24 hours"
            className="text-amber-700 dark:text-amber-400 hover:text-amber-900 dark:hover:text-amber-200"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionBanner;
